import React, { useState } from "react";
import { CheckCircle2, AlertCircle, Send } from "lucide-react";
import { submitProposal } from "../lib/api";
import { CATEGORY_LABELS } from "../lib/format";
import { useReveal } from "../lib/useReveal";

const initial = {
  submitter_name: "",
  submitter_email: "",
  proposed_title: "",
  proposed_date_label: "",
  proposed_category: "",
  rationale: "",
  sources: "",
};

export const SubmitForm = () => {
  const [form, setForm] = useState(initial);
  const [state, setState] = useState({ status: "idle", message: "" });
  const ref = useReveal();

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setState({ status: "loading", message: "" });
    try {
      const payload = {
        submitter_name: form.submitter_name || undefined,
        submitter_email: form.submitter_email || undefined,
        proposed_title: form.proposed_title.trim(),
        proposed_date_label: form.proposed_date_label || undefined,
        proposed_category: form.proposed_category || undefined,
        rationale: form.rationale.trim(),
        sources: form.sources || undefined,
      };
      if (!payload.proposed_title || payload.proposed_title.length < 2) throw new Error("Please add a date or observance name.");
      if (!payload.rationale || payload.rationale.length < 10) throw new Error("Please add a brief reason — at least a sentence.");
      await submitProposal(payload);
      setState({ status: "success", message: "Thank you. Your suggestion has been received and will be reviewed." });
      setForm(initial);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Something went wrong. Please try again.";
      setState({ status: "error", message: typeof msg === "string" ? msg : "Could not submit right now." });
    }
  };

  return (
    <section id="submit" className="section reveal" ref={ref} data-testid="submit-section">
      <div className="shell">
        <div style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1.3fr)",
          gap: "3rem",
          alignItems: "start",
        }} className="submit-grid">
          <div>
            <span className="section__eyebrow">Suggest a Date</span>
            <h2 style={{ marginTop: 0 }}>Help us keep this archive alive.</h2>
            <p style={{ fontSize: "1.05rem", lineHeight: 1.65 }}>
              If a date, observance, or anniversary belongs in this archive and isn’t here yet, tell us.
              We review every submission, prioritize entries with credible sources, and do not publish
              partisan attacks or unverifiable claims.
            </p>
            <ul style={{ paddingLeft: "1.1rem", marginTop: "1.5rem", display: "flex", flexDirection: "column", gap: 8, color: "hsl(var(--ink-soft))" }}>
              <li>Include at least one credible source link if you can.</li>
              <li>Tell us briefly why the date matters — historically, culturally, civically.</li>
              <li>Sharing your name and email is optional but helps us follow up.</li>
            </ul>
          </div>

          <form
            onSubmit={onSubmit}
            data-testid="submit-form"
            noValidate
            style={{
              border: "1px solid hsl(var(--line))",
              padding: "1.75rem",
              background: "hsl(var(--bg-elev))",
              display: "flex",
              flexDirection: "column",
              gap: "1rem",
            }}
          >
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="Your name (optional)">
                <input type="text" value={form.submitter_name} onChange={onChange("submitter_name")} style={inputStyle} data-testid="field-name" />
              </Field>
              <Field label="Email (optional)">
                <input type="email" value={form.submitter_email} onChange={onChange("submitter_email")} style={inputStyle} data-testid="field-email" />
              </Field>
            </div>

            <Field label="Date or observance" required>
              <input
                type="text"
                value={form.proposed_title}
                onChange={onChange("proposed_title")}
                required
                placeholder="e.g. Greenwood Massacre Centennial"
                style={inputStyle}
                data-testid="field-title"
              />
            </Field>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="Date or recurring time period">
                <input
                  type="text"
                  value={form.proposed_date_label}
                  onChange={onChange("proposed_date_label")}
                  placeholder="e.g. May 31–June 1"
                  style={inputStyle}
                  data-testid="field-date-label"
                />
              </Field>
              <Field label="Category">
                <select value={form.proposed_category} onChange={onChange("proposed_category")} style={inputStyle} data-testid="field-category">
                  <option value="">Choose a category</option>
                  {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </Field>
            </div>

            <Field label="Why does this date matter?" required>
              <textarea
                value={form.rationale}
                onChange={onChange("rationale")}
                required
                rows={5}
                placeholder="Tell us what happened, who it affected, and why it should be remembered."
                style={{ ...inputStyle, resize: "vertical", minHeight: 120 }}
                data-testid="field-rationale"
              />
            </Field>

            <Field label="Sources (optional, one per line)">
              <textarea
                value={form.sources}
                onChange={onChange("sources")}
                rows={2}
                placeholder="https://www.archives.gov/..."
                style={{ ...inputStyle, resize: "vertical", minHeight: 70 }}
                data-testid="field-sources"
              />
            </Field>

            {state.status === "success" && (
              <div role="status" data-testid="submit-success" style={statusStyle("success")}>
                <CheckCircle2 size={16} aria-hidden /> {state.message}
              </div>
            )}
            {state.status === "error" && (
              <div role="alert" data-testid="submit-error" style={statusStyle("error")}>
                <AlertCircle size={16} aria-hidden /> {state.message}
              </div>
            )}

            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 4 }}>
              <button
                type="submit"
                className="btn btn--primary"
                disabled={state.status === "loading"}
                data-testid="submit-button"
              >
                <Send size={15} aria-hidden />
                {state.status === "loading" ? "Submitting…" : "Submit suggestion"}
              </button>
            </div>
          </form>
        </div>
      </div>
      <style>{`
        @media (max-width: 880px) {
          .submit-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </section>
  );
};

const Field = ({ label, required, children }) => (
  <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
    <span style={{
      fontSize: "0.78rem",
      letterSpacing: "0.12em",
      textTransform: "uppercase",
      color: "hsl(var(--muted))",
      fontWeight: 500,
    }}>
      {label}{required ? " *" : ""}
    </span>
    {children}
  </label>
);

const inputStyle = {
  width: "100%",
  padding: "0.65rem 0.85rem",
  border: "1px solid hsl(var(--line))",
  background: "hsl(var(--bg))",
  color: "hsl(var(--ink))",
  fontSize: "0.95rem",
  fontFamily: "var(--font-body)",
  borderRadius: 2,
};

const statusStyle = (kind) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  padding: "0.7rem 0.9rem",
  fontSize: "0.9rem",
  border: `1px solid hsl(var(--${kind === "success" ? "accent" : "destructive"}) / 0.5)`,
  background: `hsl(var(--${kind === "success" ? "accent" : "destructive"}) / 0.08)`,
  color: kind === "success" ? "hsl(var(--accent))" : "hsl(var(--destructive))",
});
